import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { listAnalyticsSites } from '../../api/analytics';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useCapabilities } from '../../lib/capabilities';
import { useLoad } from '../../lib/useLoad';
import { type AcqTab, AnalyticsView } from '../analytics/AnalyticsView';
import { type ManageLink, manageLinkOf } from '../analytics/installForm';
import { type DashboardHostProps, DashboardView } from './DashboardView';

export type DashboardTab = 'operations' | 'analytics';

const TAB_LABELS: Record<DashboardTab, string> = {
  operations: 'Operations',
  analytics: 'Analytics',
};

/**
 * The Dashboard section: dispatch's operations figures and, beside them, the
 * website analytics — two screens about how the business is doing, one place
 * to look for them.
 *
 * Analytics is offered only once it is actually SWITCHED ON — the agent's
 * `analytics` feature is not `off` AND at least one install is enabled. Framed,
 * the host has a say first (`host.analytics`): dispatch grants the tab and the
 * ways into its settings per person, and what it withholds is not drawn here. A
 * tenant that never set analytics up sees the operations dashboard and nothing
 * else; a tab opening on an empty setup screen is noise to everyone but the one
 * person who would set it up, who gets a quiet "Set up analytics" instead.
 *
 * The switch is not a row of its own. It is handed to each screen as
 * `switcher` and drawn at the start of that screen's heading row, where there
 * is room going spare — a full-width strip for two short words cost the page a
 * band of height on every visit.
 *
 * Controlled rather than owning the tab: standalone it is the PATH
 * (`/dashboard/analytics`), because the help assistant reads the pathname as
 * handbook context and the analytics articles name that route; framed it is
 * held by EmbedDashboard, seeded from `?tab=analytics`.
 */
export function DashboardSection({
  tab,
  onTab,
  embedded = false,
  host = null,
  manageLink = null,
  onManageLinkUsed,
  initialAcqTab,
  onOpenCampaign,
}: {
  tab: DashboardTab;
  /** `correction` is the section steering off a tab it cannot show, not a choice. */
  onTab: (tab: DashboardTab, correction?: boolean) => void;
  embedded?: boolean;
  host?: DashboardHostProps | null;
  /** Analytics only: open one install's settings (AnalyticsView). */
  manageLink?: ManageLink | null;
  onManageLinkUsed?: () => void;
  /** Analytics only: the acquisition card's opening tab, and a campaign row's way to its campaign. */
  initialAcqTab?: AcqTab;
  onOpenCampaign?: (id: number) => void;
}) {
  const { me, loading } = useCapabilities();
  const level = me?.access.features.analytics;
  // The host's word on this person, when it gave one. It only takes away.
  const hostReports = host?.analytics?.reports !== false;
  const hostSettings = host?.analytics?.settings !== false;
  const permitted = me != null && level !== 'off' && hostReports;
  // A deep link is HONOURED until /meta says no — the posture of every helper
  // in lib/capabilities.ts, since the server is the real boundary. The host
  // has already answered, so its no is a no at once.
  const refused = level === 'off' || !hostReports;
  const current: DashboardTab = tab === 'analytics' && !refused ? 'analytics' : 'operations';

  // Re-read on every switch: an install created or switched on over on the
  // analytics side must put the tab there by the time the reader comes back.
  const sites = useLoad(() => (permitted ? listAnalyticsSites() : Promise.resolve([])), [permitted, current]);
  const switchedOn = (sites.data ?? []).some((site) => site.enabled);

  // A link to a tab this person cannot have lands on the one they can, and the
  // address says so rather than carrying a tab that is not on screen.
  useEffect(() => {
    if (tab !== current) onTab(current, true);
  }, [tab, current, onTab]);

  let switcher: ReactNode = null;
  // Shown while ON the analytics tab whatever the installs say, so the setup
  // screen (and an install just switched off) always has a way back.
  if ((permitted && switchedOn) || current === 'analytics') {
    switcher = (
      <div className="db-switch" role="tablist" aria-label="Dashboard">
        {(['operations', 'analytics'] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={current === key}
            className={current === key ? 'on' : ''}
            onClick={() => onTab(key)}
          >
            {TAB_LABELS[key]}
          </button>
        ))}
      </div>
    );
  } else if (permitted && hostSettings && level === 'manage' && sites.data) {
    // Only once the list has ANSWERED with nothing enabled — never on a load
    // or a failed read, where it would offer to set up what already exists.
    // Framed by a host that keeps the settings on a page of its own, setting
    // up is done THERE — the tab would only point at it. A host that keeps
    // the settings from this person gets no way to them at all.
    const openSettings = host?.openSettings;
    switcher = (
      <button
        type="button"
        className="db-switch-setup"
        onClick={() => (openSettings ? openSettings({ siteId: null, section: null }) : onTab('analytics'))}
      >
        Set up analytics
      </button>
    );
  }

  // A link straight to analytics waits for /meta rather than mounting the
  // screen on a guess: for someone refused, that guess flashed the analytics
  // screen and spent a refused read before landing them on Operations. Only
  // while /meta is in flight — one that FAILED leaves `me` null for good, and
  // then the link is honoured and the server answers.
  if (current === 'analytics' && me == null && loading) return null;

  return (
    // Keyed on the tab: a boundary keeps its error until it remounts, and the
    // shell's own is keyed on the path, which a framed switch leaves alone — a
    // crash in one tab must not follow the reader to the other.
    <ErrorBoundary key={current} label={TAB_LABELS[current]}>
      {current === 'analytics' ? (
        <AnalyticsView
          embedded={embedded}
          switcher={switcher}
          manageLink={manageLink}
          onManageLinkUsed={onManageLinkUsed}
          onOpenSettings={hostSettings ? (host?.openSettings ?? null) : null}
          settingsAllowed={hostSettings}
          initialAcqTab={initialAcqTab}
          onOpenCampaign={onOpenCampaign}
        />
      ) : (
        <DashboardView embedded={embedded} host={host} switcher={switcher} />
      )}
    </ErrorBoundary>
  );
}

/**
 * The standalone mount, at `/dashboard` and `/dashboard/analytics`. A switch
 * replaces the history entry only when it is a correction (the effect above),
 * so Back still walks between the tabs the reader actually chose.
 *
 * `/dashboard/analytics?manage=install&site=12` is the dispatch dashboard's
 * Manage in CRM for the install its Website analytics switch points at. Only
 * here, never in the framed mounts: there the address is the embed protocol.
 * Once the screen has acted on it the query is dropped in place, because where
 * a reader is inside Install and settings is deliberately not in the address.
 */
export function DashboardRoute() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const tab: DashboardTab = pathname.startsWith('/dashboard/analytics') ? 'analytics' : 'operations';
  const onTab = useCallback(
    (next: DashboardTab, correction = false) => {
      if (next === tab) return;
      navigate(next === 'analytics' ? '/dashboard/analytics' : '/dashboard', { replace: correction });
    },
    [navigate, tab],
  );
  const manageLink = useMemo(() => (tab === 'analytics' ? manageLinkOf(search) : null), [tab, search]);
  const onManageLinkUsed = useCallback(() => navigate(pathname, { replace: true }), [navigate, pathname]);
  // `?acq=campaigns` is a campaign's Results asking for the card it is counted
  // on; a row there opens the campaign back in Communications. Standalone only —
  // the framed dashboard has no Communications to send a reader to.
  const initialAcqTab = new URLSearchParams(search).get('acq') === 'campaigns' ? 'campaigns' : undefined;
  const onOpenCampaign = useCallback(
    (id: number) => navigate(`/comms?tab=campaigns&campaign=${id}`),
    [navigate],
  );
  return (
    <DashboardSection
      tab={tab}
      onTab={onTab}
      manageLink={manageLink}
      onManageLinkUsed={onManageLinkUsed}
      initialAcqTab={initialAcqTab}
      onOpenCampaign={onOpenCampaign}
    />
  );
}
